// src/contexts/geral/PrintNFCeContext.js
import React, { createContext, useContext, useCallback } from 'react';
import { useMessage } from '../../contexts/geral/MessageContext';
import { formatCurrency } from '../../utils/numberUtils';
import { useConfigModulo } from '../administrativo/ConfigModuloContext';
import { useNota } from '../vendas/NotaContext';
import { jsPDF } from 'jspdf';
import { getDanfeNFCePDFByNotaIdService } from '../../services/vendas/PDVService';

const PrintNFCeContext = createContext();

export const PrintNFCeProvider = ({ children }) => {
    const { showMessage } = useMessage();
    const { configModuloSel } = useConfigModulo();
    const { buscaNotaById } = useNota();

    const visualizarNFCePdf = useCallback(async (notaId) => {
        const janelaPdf = window.open('', '_blank');

        if (!janelaPdf) {
            showMessage('Pop-up bloqueado ao tentar abrir o PDF da NFC-e.', 'warning');
            return;
        }

        try {
            const blob = await getDanfeNFCePDFByNotaIdService({ notaId, paperWidthMm: 72 });
            const url = URL.createObjectURL(blob);
            janelaPdf.location.href = url;

            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (error) {
            janelaPdf.close();
            showMessage('Erro ao abrir o PDF da NFC-e.', 'danger');
            console.error(error);
        }
    }, [showMessage]);

    const gerarRecibo = useCallback(async (notaId) => {
        try {
            const nota = await buscaNotaById(notaId);

            if (!nota.notaItens?.length) {
                showMessage('Nenhum item na venda para imprimir.', 'warning');
                return;
            }

            const doc = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: [72, 300]
            });

            doc.setFont('courier');
            doc.setFontSize(10);
            const margin = 0;
            let y = margin;

            const addHr = () => {
                doc.setLineWidth(0.5);
                doc.setDrawColor(0);
                doc.line(margin, y, 72 - margin, y);
                y += 4;
            };

            const addCenteredText = (text, fontSize = 10, isBold = false) => {
                doc.setFontSize(fontSize);
                doc.setFont('courier', isBold ? 'bold' : 'normal');
                const textWidth = doc.getTextWidth(text);
                const x = (72 - textWidth) / 2;
                doc.text(text, x, y);
                y += fontSize * 0.4;
            };

            addHr();
            addCenteredText('RECIBO DE VENDA', 12, true);
            addHr();

            doc.setFontSize(10);
            doc.setFont('courier', 'bold');
            doc.text('Caixa:', margin, y);
            doc.setFont('courier', 'normal');
            doc.text(nota.caixa?.descricao || 'N/A', margin + doc.getTextWidth('Caixa: ') + 1, y);
            y += 10 * 0.4;
            addHr();

            addCenteredText('Itens', 12, true);
            if (configModuloSel.printValUnRec) {
                doc.setFontSize(9);
                const headers = ['SEQ', 'DESCRICAO', 'QTDE', 'UN', 'UNIT', 'TOTAL'];
                const xPositions = [margin, margin + 9.6, margin + 44.4, margin + 56.4, margin + 66, margin + 78];

                headers.forEach((header, i) => {
                    const align = i >= 2 ? 'right' : 'left';
                    doc.text(header, xPositions[i], y, { align });
                });
                y += 2;
                doc.line(margin, y, 72 - margin, y);
                y += 2;

                nota.notaItens.forEach((item) => {
                    const descricaoTruncada = item?.xProd?.substring(0, 24).toUpperCase() || '';
                    const row = [
                        String(item?.item).padStart(3, '0'),
                        descricaoTruncada,
                        item?.qtdCom.toFixed(3).replace('.', ','),
                        item?.unCom,
                        item?.vUnCom.toFixed(2).replace('.', ','),
                        item?.vProd.toFixed(2).replace('.', ',')
                    ];
                    row.forEach((cell, i) => {
                        const align = i >= 2 ? 'right' : 'left';
                        doc.text(cell, xPositions[i], y, { align });
                    });
                    y += 4;
                });
                doc.line(margin, y, 72 - margin, y);
                y += 2;
            } else {
                doc.setFontSize(11);
                nota.notaItens.forEach((item) => {
                    const descricao = `${item.qtdCom}x ${item?.xProd.toUpperCase()}`;
                    const descricaoTruncada = descricao.substring(0, 43) || '';
                    const total = formatCurrency(item.vProd);
                    doc.text(descricaoTruncada, margin, y);
                    doc.text(total, 72 - margin, y, { align: 'right' });
                    y += 4;
                });
            }

            addHr();
            if (nota.vFrete) {
                addCenteredText(`Frete: ${formatCurrency(nota.vFrete)}`, 10, true);
            }
            addCenteredText(`Total: ${formatCurrency(nota.vNota)}`, 10, true);
            addHr();

            if (nota.observacao && nota.observacao.trim() !== '') {
                addCenteredText('OBSERVACAO', 10, true);
                const linhas = doc.splitTextToSize(nota.observacao, 68);
                linhas.forEach((line) => {
                    doc.text(line, margin, y);
                    y += 4;
                });
                addHr();
            }

            addCenteredText('OBRIGADO, VOLTE SEMPRE!', 10, true);
            doc.setPage(1);
            doc.setProperties({ pageSize: [72, y + margin] });
            doc.save(`Recibo_${nota.numDoc}.pdf`);
        } catch (error) {
            showMessage('Erro ao gerar o PDF do recibo.', 'danger');
            console.error(error);
        }
    }, [showMessage, configModuloSel, buscaNotaById]);

    return (
        <PrintNFCeContext.Provider value={{ visualizarNFCePdf, gerarRecibo }}>
            {children}
        </PrintNFCeContext.Provider>
    );
};

export const usePrintNFCe = () => {
    const context = useContext(PrintNFCeContext);
    if (!context) {
        throw new Error('usePrintNFCe must be used within a PrintNFCeContext');
    }
    return context;
};
